<template>
  <motlet-neo :options="motletOptions">
    <template v-slot:content="{ props }">
      <bar-chart ref="barChart" v-if="isBarChart" :settings="settings" :motlet="props.motlet" :store-name="storeName" />
      <!-- eslint-disable-next-line prettier/prettier -->
      <line-chart
        v-else
        :settings="settings"
        :motlet="props.motlet"
        :store-name="storeName"
        :active-increment="activeIncrement"
      />
    </template>
  </motlet-neo>
</template>

<script>
// BEGIN::Modified.by.SHJ.2026.04.28. PcapFilesystem 패턴을 non-Pcap에 적용.
// WS key: rawOsFsys, bar motletCode: C_ABAP_OS_FSYS, line motletCode: C_ABAP_OS_FSYS_NAME.
// 데이터 필드: raw OsFileSystem 모델 — fsysname, capacity, free.
import { merge } from 'lodash';
import { format } from 'date-fns';
import { api } from '@/api/common/instances';
import bus from '@/utils/bus';
import BarChart from '../../../components/BarChart';
import lineChartable from './lineChartable';

export default {
  components: { BarChart },
  mixins: [lineChartable],
  props: {
    timestamp: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      barData: [],
      socketData: [],
      socketFlag: [],
      currentFsysType: 'ALL',
      currentChartType: 'bar',
      motletOptions: {
        id: 'dv/abap/os/fsys',
        title: 'dv.title.fsys',
        monitor: { monitorCode: 'C_ABAP_OS_FSYS', eventCode: 'ABAP_OS_FSYS', points: 60, categoryField: 'fsysname', valueField: 'usedRate' },
        settings: { chart: { use: true } },
        fsysType: {
          use: true,
          types: [{ code: 'ALL', text: 'ALL' }],
          selected: 'ALL',
        },
        chartType: {
          use: true,
          selected: 'bar',
        },
        xAxisPoints: {
          use: true,
          divider: true,
          ready: false,
          value: 60,
          default: 60,
        },
      },
    };
  },
  computed: {
    isBarChart() {
      return this.currentChartType === 'bar';
    },
    fsysNames() {
      const set = new Set();
      this.barData.forEach(item => {
        item.data.forEach(d => {
          if (d.fsysname) set.add(d.fsysname);
        });
      });
      return [...set].sort();
    },
    filteredBarData() {
      const fsysType = this.currentFsysType;
      return this.barData.map(item => ({
        id: item.id,
        data: fsysType === 'ALL' ? item.data : item.data.filter(d => d.fsysname === fsysType),
      }));
    },
    settings() {
      if (this.isBarChart) {
        if (this.filteredBarData.length === 0 || !this.filteredBarData.some(d => d.data.length > 0)) {
          return {};
        }
        return this.buildBarSettings();
      }
      if (this.data.length === 0 || this.currentFsysType === 'ALL') return {};
      const fieldKey = this.currentFsysType;
      const yValue = item => {
        const val = item[fieldKey];
        return val === '' || val === undefined ? '' : val;
      };
      const xAxis = {
        data: this.data[0].data.map(({ ctime }) => ctime),
        axisLabel: {
          interval: Math.ceil(this.data[0].data.length / 6),
        },
      };
      const yAxis = {
        name: 'Used (%)',
        max: 100,
        maxInterval: 25,
        splitNumber: 4,
      };
      const series = this.data.map(item => {
        const s = {
          type: 'line',
          id: item.id,
          lineStyle: {},
          data: item.data.map((d, index) => [index, yValue(d), d.ctime]),
        };
        const descendant = this.descendants.find(d => d.id == item.id);
        if (!descendant) return s;
        return merge({}, s, {
          lineStyle: {
            color: descendant.color,
            opacity: descendant.active ? 1 : 0.1,
          },
        });
      });
      const valueFormatter = this.getValueFormatter();
      const formatter = {
        tooltip: params => {
          const param = params[0];
          const contents = [];
          contents.push('<i class="v-icon notranslate mdi mdi-clock-time-four-outline mr-1" style="font-size: 12px; line-height: 10px"></i>');
          contents.push(format(parseInt(param.name), 'yyyy-MM-dd HH:mm:ss'));
          contents.push('<br />');
          const data = params.reduce((acc, p) => {
            const descendant = this.descendants.find(d => d.id == p.seriesId);
            if ((descendant || {}).active) {
              const items = [];
              items.push(this.circle(descendant.color));
              items.push(`<span style="color:${descendant.color};font-weight:900;">`);
              items.push(descendant.shortAlias);
              items.push('</span> : ');
              items.push(valueFormatter(p.data[1]));
              items.push('<br />');
              acc.push(items.join(''));
            }
            return acc;
          }, []);
          return contents.concat(data).join('');
        },
      };
      return merge(
        {},
        {
          chart: { xAxis, yAxis, series },
          formatter,
          systemId: this.sstmId,
          storeName: this.storeName,
          datetime: this.datetime,
        },
      );
    },
  },
  watch: {
    timestamp: {
      immediate: true,
      handler() {
        if (this.isBarChart) this.fetchBarData();
      },
    },
    filteredBarData: {
      deep: true,
      handler() {
        if (!this.isBarChart) return;
        this.$nextTick(() => {
          const echart = this.$refs.barChart?.echart;
          if (echart) {
            echart.setOption({ dataset: { source: [] }, series: [] });
            echart.setOption(this.$refs.barChart.options);
          }
        });
      },
    },
    sstmId() {
      if (!this.isBarChart && this.activeIncrement && this.motletOptions.xAxisPoints.ready) this.sendMessage();
    },
    activeIncrement: {
      immediate: true,
      handler(value) {
        if (!this.isBarChart && value && this.motletOptions.xAxisPoints.ready) this.sendMessage();
      },
    },
    currentChartType(newType) {
      if (newType === 'bar') {
        this.fetchBarData();
      } else {
        if (!this.motletOptions.xAxisPoints.ready) return;
        if (this.activeIncrement) {
          if (this.data.length === 0) {
            this.sendMessage();
          } else {
            this.backfillFromDb();
          }
        } else {
          this.fetchData();
        }
      }
    },
    // BEGIN::Added.by.SHJ.2026.05.13. 언어 변경 시 헤더 타이틀 재합성
    '$i18n.locale'() {
      this.updateTitle();
    },
    // FINISH::Added.by.SHJ.2026.05.13.
  },
  methods: {
    getValueFormatter() {
      return data => {
        const isEmpty = data === undefined || data === null || data === '';
        return isEmpty ? '' : `${data} %`;
      };
    },
    async fetchBarData() {
      if (!this.timestamp) return;
      const params = {
        endYmdhms: this.timestamp,
        sstmId: this.sstmId,
        motletCode: this.monitor.monitorCode,
      };
      try {
        const { data: results } = await api.get('/motlet-data/chart', { params });
        this.barData = results.map(result => {
          const {
            key: { instId: id },
            data,
          } = result;
          return { id, data };
        });
        this.refreshFsysTypeOptions();
      } catch (e) {
        console.warn('[Filesystem] fetchBarData failed', e);
      }
    },
    refreshFsysTypeOptions() {
      this.motletOptions.fsysType.types = [{ code: 'ALL', text: 'ALL' }].concat(this.fsysNames.map(name => ({ code: name, text: name })));
      const exists = this.currentFsysType === 'ALL' || this.fsysNames.includes(this.currentFsysType);
      if (!exists) {
        this.currentFsysType = 'ALL';
        this.motletOptions.fsysType.selected = 'ALL';
        if (this.currentChartType === 'line') {
          this.currentChartType = 'bar';
          this.motletOptions.chartType.selected = 'bar';
        }
      }
    },
    buildBarSettings() {
      const xAxis = {};
      const yAxis = {
        name: 'Used (%)',
        max: 100,
        maxInterval: 25,
        splitNumber: 4,
      };
      const dataset = (() => {
        const categories = [...new Set(this.filteredBarData.reduce((acc, item) => acc.concat(item.data.map(s => s.fsysname)), []))].sort();
        const header = ['name'].concat(this.descendants.map(d => d.id));
        const rows = categories.map(category => {
          return [category].concat(
            this.descendants
              .filter(d => d.active)
              .map(d => {
                const index = this.descendants.findIndex(item => item.id == d.id);
                const target = this.filteredBarData[index] ? this.filteredBarData[index].data.find(item => item.fsysname == category) : null;
                return (target || {}).usedRate;
              }),
          );
        });
        return { source: [header].concat(rows) };
      })();
      const series = this.descendants
        .filter(d => d.active)
        .map(d => ({
          name: d.id,
          type: 'bar',
          itemStyle: { color: d.color },
        }));
      const valueFormatter = this.getValueFormatter();
      const formatter = {
        tooltip: params => {
          const param = params[0];
          const contents = [];
          contents.push('<i class="v-icon notranslate mdi mdi-harddisk mr-1" style="font-size: 12px; line-height: 10px"></i>');
          contents.push(param.name);
          contents.push('<br />');
          params.forEach((p, index) => {
            if (p.data[index + 1] === undefined) return;
            const color = this.descendants.find(d => d.id == p.seriesName).color;
            contents.push(this.circle(color));
            contents.push(`<span style="color:${color};font-weight:900;">`);
            contents.push(this.descendants.find(d => d.id == p.seriesName).shortAlias);
            contents.push('</span> : ');
            contents.push(valueFormatter(p.data[index + 1]));
            contents.push('<br />');
          });
          return contents.join('');
        },
      };
      return { chart: { xAxis, yAxis, dataset, series }, formatter };
    },
    /**
     * raw OsFileSystem 모델: fsysname, capacity, free → usedRate = (1 - free/capacity) * 100
     * (RawOsFileSystemMapper.xml의 SQL 공식과 일치)
     */
    dataHandler(response, ctime, empty) {
      const ctimeVal = parseInt(ctime) - (parseInt(ctime) % this.monitor.cycle);
      const data = {
        ctime: ctimeVal,
        stime: parseInt(ctime),
      };
      this.fsysNames.forEach(name => {
        data[name] = '';
      });
      if (!empty && Array.isArray(response)) {
        response.forEach(r => {
          const capacity = parseInt(r.capacity);
          const free = parseInt(r.free);
          const usedRate = capacity > 0 ? Math.ceil((1 - free / capacity) * 100) : 0;
          if (r.fsysname) data[r.fsysname] = usedRate;
        });
      }
      return data;
    },
    wsBufferedDataHandler(key, responses) {
      const [response] = responses;
      const data = response.map(item => this.dataHandler(item.data, item.ctime, item.empty));
      const existing = this.socketData.findIndex(s => s.id === key.instId);
      if (existing >= 0) {
        this.socketData[existing] = { id: key.instId, data };
      } else {
        this.socketData.push({ id: key.instId, data });
      }
      if (this.socketData.length == this.descendants.length) {
        this.data = merge([], this.socketData);
        this.socketData = [];
        this.socketFlag = this.descendants.map(d => ({ id: d.id, arrived: false }));
        this.prepend(this.fsysNames);
        if (this.data && this.data.length > 0) {
          bus.$emit(`change:timestamp-${this.storeName}`, {
            eventCode: this.motletOptions.monitor.eventCode,
            timestamp: this.data[this.data.length - 1].ctime,
          });
        }
        this.backfillFromDb();
      }
    },
    wsDataHandler(key, response, ctime, empty) {
      if (this.data.length === 0) return;
      if (ctime === null || ctime === undefined) return;
      const data = this.dataHandler(response, ctime, empty);
      const target = this.data.find(d => d.id == key.instId);
      if (target) {
        target.data.push(data);
        target.data.splice(0, 1);
      }
      const inst = this.socketFlag.find(i => i.id == key.instId);
      if (inst && inst.arrived) {
        this.socketFlag.forEach(i => {
          if (i.arrived == false) {
            const t = this.data.find(d => d.id == i.id);
            if (t) {
              const emptySlot = { ctime: parseInt(ctime), stime: parseInt(ctime) };
              this.fsysNames.forEach(name => {
                emptySlot[name] = undefined;
              });
              t.data.push(emptySlot);
              t.data.splice(0, 1);
            }
          }
          if (i.id == key.instId) i.arrived = true;
          else i.arrived = false;
        });
      } else if (inst) {
        inst.arrived = true;
      }
    },
    async backfillFromDb() {
      if (this.isBarChart) return;
      if (this.currentFsysType === 'ALL') return;
      if (!this.data || this.data.length === 0) return;
      const firstInst = this.data[0].data;
      if (!firstInst || firstInst.length === 0) return;
      const fieldKey = this.currentFsysType;
      const realPoints = firstInst.filter(d => d[fieldKey] !== undefined && d[fieldKey] !== '');
      if (realPoints.length === firstInst.length) return;
      const beginYmdhms = firstInst[0].ctime;
      const endYmdhms = realPoints.length > 0 ? realPoints[0].ctime - this.monitor.cycle : firstInst[firstInst.length - 1].ctime;
      if (endYmdhms < beginYmdhms) return;
      const params = {
        beginYmdhms,
        endYmdhms,
        sstmId: this.sstmId,
        motletCode: 'C_ABAP_OS_FSYS_NAME',
        fsysName: this.currentFsysType,
        ...this.dataParam,
      };
      try {
        const { data: results } = await api.get('/motlet-data/chart', { params });
        if (!results || results.length === 0) return;
        results.forEach(result => {
          const inst = this.data.find(d => d.id === result.key.instId);
          if (!inst) return;
          result.data.forEach(db => {
            const dbCtime = parseInt(db.ctime);
            const slot = inst.data.find(p => p.ctime === dbCtime);
            if (slot && (slot[fieldKey] === undefined || slot[fieldKey] === '')) {
              slot[fieldKey] = parseInt(db.usedRate);
              if (db.stime) slot.stime = parseInt(db.stime);
            }
          });
        });
      } catch (e) {
        console.warn('[Filesystem] backfillFromDb failed', e);
      }
    },
    async fetchData() {
      if (this.isBarChart) return;
      if (this.activeIncrement) return;
      if (!this.motletOptions.xAxisPoints.ready) return;
      if (this.currentFsysType === 'ALL') return;
      const bounds = this.getBounds();
      if (!bounds) return;
      const params = {
        ...bounds,
        sstmId: this.sstmId,
        motletCode: 'C_ABAP_OS_FSYS_NAME',
        fsysName: this.currentFsysType,
        ...this.dataParam,
      };
      const { data: results } = await api.get('/motlet-data/chart', { params });
      const fieldKey = this.currentFsysType;
      this.data = results.map(result => {
        const {
          key: { instId: id },
          data,
        } = result;
        const sortedData = data
          .map(item => {
            const ctime = parseInt(item.ctime) || parseInt(item.stime);
            const slot = { ctime, stime: parseInt(item.stime) || ctime };
            this.fsysNames.forEach(name => {
              slot[name] = '';
            });
            slot[fieldKey] = parseInt(item.usedRate);
            return slot;
          })
          .sort((a, b) => a.ctime - b.ctime);
        return { id, data: sortedData };
      });
    },
    sendMessage() {
      this.socketData = [];
      this.$websocket.realtime.send({
        requests: this.descendants.map(descendant => ({
          key: { name: 'rawOsFsys', sstmId: descendant.systemId, instId: descendant.id },
          fields: ['stime', 'fsysname', 'capacity', 'free'],
          bufferSize: this.monitor.points,
          handler: {
            buffer: this.wsBufferedDataHandler,
            every: this.wsDataHandler,
          },
        })),
      });
    },
    xPointsChanged(xPoints) {
      this.motletOptions.xAxisPoints.ready = true;
      this.motletOptions.xAxisPoints.value = xPoints;
      this.motletOptions.monitor.points = xPoints;
      this.setXAxisPoints(xPoints);
      this.setMonitor(this.motletOptions.monitor);
      if (!this.isBarChart && this.activeIncrement) this.sendMessage();
    },
    fsysTypeChanged(type) {
      this.currentFsysType = type;
      this.motletOptions.fsysType.selected = type;
      if (type === 'ALL' && this.currentChartType === 'line') {
        this.currentChartType = 'bar';
        this.motletOptions.chartType.selected = 'bar';
      }
      this.updateTitle();
      if (!this.isBarChart && type !== 'ALL') {
        if (!this.activeIncrement) {
          this.fetchData();
        } else {
          this.backfillFromDb();
        }
      }
    },
    chartTypeChanged(type) {
      this.currentChartType = type;
      this.motletOptions.chartType.selected = type;
    },
    updateTitle() {
      const suffix = this.currentFsysType === 'ALL' ? '' : ` (${this.currentFsysType})`;
      this.motletOptions.title = `${this.$t('dv.title.fsys')}${suffix}`;
    },
  },
  created() {
    bus.$on(`update:fsysType-${this.motletOptions.id}`, type => this.fsysTypeChanged(type));
    bus.$on(`update:chartType-${this.motletOptions.id}`, type => this.chartTypeChanged(type));
    bus.$on(`update:xAxisPoints-${this.motletOptions.id}`, xAxisPoints => {
      bus.$emit('update:activeIncrement', false);
      this.xPointsChanged(xAxisPoints);
    });
  },
  destroyed() {
    bus.$off(`update:fsysType-${this.motletOptions.id}`);
    bus.$off(`update:chartType-${this.motletOptions.id}`);
    bus.$off(`update:xAxisPoints-${this.motletOptions.id}`);
  },
};
// FINISH::Modified.by.SHJ.2026.04.28.
</script>
